const PRESET_LABELS = {
  full: "Full upfront",
  "50_50": "50 / 50",
  per_phase: "Even split",
  custom: "Custom",
};

function asCentavos(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
}

function splitEven(total, count) {
  const safeCount = Math.max(0, Number(count) || 0);
  if (safeCount === 0) return [];
  const base = Math.floor(total / safeCount);
  let remainder = total - (base * safeCount);
  return Array.from({ length: safeCount }, () => {
    const extra = remainder > 0 ? 1 : 0;
    remainder -= extra;
    return base + extra;
  });
}

function visitPhasesFromRows(visits = [], items = []) {
  const itemById = new Map((items || []).map((item) => [item.id, item]));
  const phases = new Map();

  for (const visit of visits || []) {
    const parent = itemById.get(visit.item_id) || {};
    const phaseNumber = Number(visit.phase_number ?? parent.phase_number ?? 1) || 1;
    if (!phases.has(phaseNumber)) phases.set(phaseNumber, []);
    phases.get(phaseNumber).push({ ...visit, parent });
  }

  return Array.from(phases.entries())
    .sort(([a], [b]) => a - b)
    .map(([phaseNumber, rows], index) => ({
      phaseNumber,
      visitNumber: index + 1,
      rows,
      completed: rows.length > 0 && rows.every((row) => !!row.completed_at),
      booked: rows.some((row) => !!row.appointment_id),
    }));
}

function plannedAmountsForPreset({ preset, total, visitCount, customSchedule }) {
  if (visitCount <= 0 || total <= 0) return [];

  if (preset === "full") {
    return [total, ...Array.from({ length: Math.max(0, visitCount - 1) }, () => 0)];
  }

  if (preset === "50_50") {
    if (visitCount === 1) return [total];
    const first = Math.round(total / 2);
    const middle = Array.from({ length: Math.max(0, visitCount - 2) }, () => 0);
    return [first, ...middle, total - first];
  }

  if (preset === "per_phase") {
    return splitEven(total, visitCount);
  }

  if (preset === "custom") {
    if (visitCount === 1) return [total];
    const dp = Math.min(total, asCentavos(customSchedule?.dp_centavos));
    const rest = splitEven(total - dp, visitCount - 1);
    return [dp, ...rest];
  }

  return [];
}

export function buildTxPlanPaymentSchedule({ billingState, visits = [], items = [] } = {}) {
  if (!billingState) {
    return {
      rows: [],
      preset: null,
      presetLabel: null,
      message: "Billing state is not available yet.",
    };
  }

  const preset = billingState.preset || null;
  const phases = visitPhasesFromRows(visits, items);
  const visitCount = phases.length || Number(billingState.total_visits || 0);
  const total = asCentavos(
    billingState.final_total_centavos
      ?? billingState.plan_total_items_centavos
      ?? billingState.total_centavos,
  );
  const paid = asCentavos(billingState.paid_centavos);

  if (!preset) {
    return {
      rows: [],
      preset,
      presetLabel: null,
      message: "Set a billing model to show the visit payment schedule.",
    };
  }

  const planned = plannedAmountsForPreset({
    preset,
    total,
    visitCount,
    customSchedule: billingState.custom_schedule || {},
  });

  let remainingPaid = paid;
  const rows = planned.map((plannedCentavos, index) => {
    const phase = phases[index] || {
      phaseNumber: index + 1,
      visitNumber: index + 1,
      rows: [],
      completed: false,
      booked: false,
    };
    const paidCentavos = Math.min(plannedCentavos, remainingPaid);
    remainingPaid = Math.max(0, remainingPaid - paidCentavos);
    const dueCentavos = Math.max(0, plannedCentavos - paidCentavos);
    return {
      key: `${phase.phaseNumber}-${index}`,
      visitNumber: phase.visitNumber,
      phaseNumber: phase.phaseNumber,
      plannedCentavos,
      paidCentavos,
      dueCentavos,
      completed: phase.completed,
      booked: phase.booked,
      procedureCount: phase.rows.length,
      status: "upcoming",
    };
  });

  // Splitting a total across visits leaves sub-peso remainders: ₱200,000
  // over 3 visits plans [66,666.67, 66,666.67, 66,666.66], so a patient who
  // paid exactly ₱66,666.66 for visit 1 is left owing one centavo. That is
  // not a debt — but it is > 0, so it used to brand a fully-paid completed
  // visit "Overdue" while the amount beside it rounded to ₱0.
  //
  // Classify on the same rounded peso the row displays, so a chip can never
  // contradict the number next to it. This changes labels only; the plan's
  // real outstanding balance is untouched and still comes from billing_state.
  const isSettled = (row) => Math.round(row.dueCentavos / 100) <= 0;
  const firstDueIndex = rows.findIndex((row) => !isSettled(row));
  return {
    rows: rows.map((row, index) => {
      let status = "upcoming";
      if (row.plannedCentavos <= 0) status = "no_due";
      else if (isSettled(row)) status = "paid";
      else if (row.completed) status = "overdue";
      else if (index === firstDueIndex) status = "due_next";
      return { ...row, status };
    }),
    preset,
    presetLabel: PRESET_LABELS[preset] || preset,
    message: rows.length === 0 ? "No planned visits to schedule payments against." : null,
  };
}
